/**
 * Redux Saga for OTA updates
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';
import * as UpdateActions from '../actions/updateAction';
import UpdateService from '../../services/updateService';
import UpdateDownloadManager from '../../services/updateDownloadManager';
import { IUpdateManifest, IUpdateComponent, IInstalledVersions } from '../../models/Update';
import { reportUpdateFailed, reportUpdateInstalled } from '../../api/updates';
import { isElectron } from '../../utils/miscUtils';

const OTA_LOG_PREFIX = '[OTA Saga]';
const CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY = '@cuda_auto_download_attempted_version';

function isWindowsElectron(): boolean {
  try {
    const platform = (typeof window !== 'undefined' && (window as any)?.electron?.platform) || null;
    return isElectron() && platform === 'win32';
  } catch {
    return false;
  }
}

function* checkUpdatesSaga() {
  console.log(`${OTA_LOG_PREFIX} checkUpdatesSaga started`);
  try {
    // Backend OTA manifest currently targets desktop/electron flow.
    // Skip mobile/web checks to avoid noisy "Invalid platform" API errors.
    if (!isElectron()) {
      console.log(`${OTA_LOG_PREFIX} Skipping OTA manifest check on non-Electron platform`);
      yield put(
        UpdateActions.checkUpdates.success(
          {
            version: '',
            releaseDate: '',
            minAppVersion: '',
            updates: [],
          },
          []
        )
      );
      return;
    }

    // First, get the installed versions to update currentVersion in redux
    console.log(`${OTA_LOG_PREFIX} Getting installed versions to update currentVersion...`);
    const installedVersions: IInstalledVersions = yield call([UpdateService, 'getInstalledVersions']);
    if (installedVersions?.appVersion) {
      console.log(`${OTA_LOG_PREFIX} Setting currentVersion to: ${installedVersions.appVersion}`);
      yield put(UpdateActions.setCurrentVersion(installedVersions.appVersion));
    }

    console.log(`${OTA_LOG_PREFIX} Calling UpdateService.checkForUpdates...`);
    const manifest: IUpdateManifest = yield call([UpdateService, 'checkForUpdates']);

    if (manifest) {
      console.log(`${OTA_LOG_PREFIX} Manifest received, filtering applicable updates...`);
      const availableUpdates: IUpdateComponent[] = yield call(
        [UpdateService, 'filterApplicableUpdates'],
        manifest
      );

      console.log(`${OTA_LOG_PREFIX} Check complete: ${availableUpdates.length} updates available`);
      yield put(UpdateActions.checkUpdates.success(manifest, availableUpdates));
    } else {
      console.log(`${OTA_LOG_PREFIX} No manifest returned, no updates available`);
      yield put(
        UpdateActions.checkUpdates.success(
          {
            version: '',
            releaseDate: '',
            minAppVersion: '',
            updates: [],
          },
          []
        )
      );
    }
    console.log(`${OTA_LOG_PREFIX} checkUpdatesSaga completed successfully`);
  } catch (error: any) {
    console.error(`${OTA_LOG_PREFIX} checkUpdatesSaga failed:`, error);
    yield put(UpdateActions.checkUpdates.failure(error.message || 'Failed to check for updates'));
  }
}

function createDownloadChannel(update: IUpdateComponent) {
  console.log(`${OTA_LOG_PREFIX} Creating download channel for ${update.type} v${update.version}`);
  return eventChannel<{ type: 'progress'; progress: any } | { type: 'success'; path: string } | { type: 'error'; error: any }>(
    (emitter) => {
      console.log(`${OTA_LOG_PREFIX} Download channel initialized, starting download...`);
      UpdateDownloadManager.downloadUpdate(update, (progress) => {
        emitter({ type: 'progress', progress });
      })
        .then((downloadedPath) => {
          console.log(`${OTA_LOG_PREFIX} Download channel received success: ${downloadedPath}`);
          emitter({ type: 'success', path: downloadedPath });
          emitter(END);
        })
        .catch((error) => {
          console.error(`${OTA_LOG_PREFIX} Download channel received error:`, error);
          emitter({ type: 'error', error });
          emitter(END);
        });

      return () => {
        console.log(`${OTA_LOG_PREFIX} Download channel cleanup triggered`);
        // Best-effort cancellation if the saga is torn down.
        UpdateDownloadManager.cancelDownload(update.type, update.version).catch(() => {});
      };
    }
  );
}

function* downloadUpdateSaga(action: ReturnType<typeof UpdateActions.downloadUpdate.request>) {
  const { update } = action.payload;
  console.log(`${OTA_LOG_PREFIX} downloadUpdateSaga started for ${update.type} v${update.version}`);

  try {
    if (update.type === 'ml_runtime_cuda' && isElectron()) {
      const electronAPI = (window as any)?.electronAPI;
      if (electronAPI?.checkCudaCompatibility) {
        const compat = yield call([electronAPI, 'checkCudaCompatibility']);
        if (!compat?.success) {
          throw new Error(compat?.error || 'Unable to verify CUDA compatibility');
        }
        if (!compat?.compatible) {
          const gpuSummary = Array.isArray(compat?.gpus) && compat.gpus.length ? ` Detected GPU(s): ${compat.gpus.join(', ')}` : '';
          throw new Error(`CUDA runtime is not compatible with this machine.${gpuSummary}`);
        }
      }
    }

    const channel = yield call(createDownloadChannel, update);
    console.log(`${OTA_LOG_PREFIX} Download channel created, waiting for events...`);
    while (true) {
      const event: any = yield take(channel);
      if (!event) continue;
      if (event.type === 'progress') {
        yield put(UpdateActions.downloadUpdate.progress(event.progress));
        continue;
      }
      if (event.type === 'success') {
        console.log(`${OTA_LOG_PREFIX} Download success for ${update.type}, path: ${event.path}`);
        yield put(UpdateActions.downloadUpdate.success(update, event.path));
        break;
      }
      if (event.type === 'error') {
        console.error(`${OTA_LOG_PREFIX} Download error event for ${update.type}:`, event.error);
        throw event.error;
      }
    }
    console.log(`${OTA_LOG_PREFIX} downloadUpdateSaga completed for ${update.type}`);
  } catch (error: any) {
    console.error(`${OTA_LOG_PREFIX} downloadUpdateSaga failed for ${update.type}:`, error);
    yield put(UpdateActions.downloadUpdate.failure(update, error.message || 'Download failed'));
  }
}

function* cancelDownloadSaga(action: ReturnType<typeof UpdateActions.cancelDownload>) {
  const { updateType, version } = action.payload;
  console.log(`${OTA_LOG_PREFIX} cancelDownloadSaga called for ${updateType} v${version}`);

  try {
    yield call([UpdateDownloadManager, 'cancelDownload'], updateType, version);
    console.log(`${OTA_LOG_PREFIX} Download cancelled successfully`);
  } catch (error: any) {
    console.error(`${OTA_LOG_PREFIX} Error cancelling download:`, error);
  }
}

function* applyUpdatesSaga() {
  console.log(`${OTA_LOG_PREFIX} applyUpdatesSaga started`);
  try {
    console.log(`${OTA_LOG_PREFIX} Getting pending updates...`);
    const pendingUpdates: Array<{
      update: IUpdateComponent;
      downloadedPath: string;
      timestamp: number;
    }> = yield call([UpdateService, 'getPendingUpdates']);

    console.log(`${OTA_LOG_PREFIX} Found ${pendingUpdates.length} pending updates`);

    if (pendingUpdates.length === 0) {
      console.log(`${OTA_LOG_PREFIX} No pending updates to apply`);
      yield put(UpdateActions.applyUpdates.success());
      return;
    }

    const electronEnv = isElectron();
    console.log(`${OTA_LOG_PREFIX} Running in Electron: ${electronEnv}`);

    if (electronEnv) {
      const electronAPI = (window as any)?.electronAPI;
      if (!electronAPI) {
        console.error(`${OTA_LOG_PREFIX} Electron API not available`);
        throw new Error('Electron update API unavailable');
      }

      let shouldRestart = false;

      for (const pending of pendingUpdates) {
        const { update, downloadedPath } = pending;
        console.log(`${OTA_LOG_PREFIX} Applying update: ${update.type} v${update.version} from ${downloadedPath}`);
        try {
          if (update.type === 'js_bundle') {
            console.error(`${OTA_LOG_PREFIX} JS bundle updates not supported on Electron`);
            throw new Error('JS bundle OTA updates are not supported on Electron');
          }

          console.log(`${OTA_LOG_PREFIX} Calling electronAPI.applyUpdate...`);
          const result = yield call([electronAPI, 'applyUpdate'], update.type, downloadedPath);
          console.log(`${OTA_LOG_PREFIX} applyUpdate result:`, result);

          if (!result?.success) {
            console.error(`${OTA_LOG_PREFIX} Apply update failed: ${result?.message}`);
            throw new Error(result?.message || 'Failed to apply update');
          }

          console.log(`${OTA_LOG_PREFIX} Updating installed version...`);
          yield call([UpdateService, 'updateInstalledVersion'], update.type, update.version);

          console.log(`${OTA_LOG_PREFIX} Reporting update installed...`);
          yield call(reportUpdateInstalled, update.type, update.version);

          // Best-effort cleanup of downloaded artifact
          console.log(`${OTA_LOG_PREFIX} Cleaning up downloaded file: ${downloadedPath}`);
          yield call([electronAPI, 'deleteFile'], downloadedPath);

          if (update.type === 'algo_code') {
            console.log(`${OTA_LOG_PREFIX} Algo code updated, restart required`);
            shouldRestart = true;
          }

          console.log(`${OTA_LOG_PREFIX} Successfully applied ${update.type} v${update.version}`);
        } catch (error: any) {
          console.error(`${OTA_LOG_PREFIX} Failed to apply ${update.type}:`, error);
          yield call(reportUpdateFailed, update.type, update.version, error.message || 'Apply failed');
          throw error;
        }
      }

      console.log(`${OTA_LOG_PREFIX} Clearing pending updates...`);
      yield call([UpdateService, 'clearPendingUpdates']);
      yield put(UpdateActions.applyUpdates.success());

      if (shouldRestart) {
        console.log(`${OTA_LOG_PREFIX} Restarting app...`);
        yield call([electronAPI, 'restartApp']);
      }

      console.log(`${OTA_LOG_PREFIX} applyUpdatesSaga completed successfully`);
      return;
    }

    // Non-Electron platforms: keep the pending updates, but mark the request as completed.
    // React Native JS bundle OTA requires a dedicated integration (e.g., CodePush/Expo Updates).
    console.log(`${OTA_LOG_PREFIX} Non-Electron platform, marking as success (no actual apply)`);
    yield put(UpdateActions.applyUpdates.success());
  } catch (error: any) {
    console.error(`${OTA_LOG_PREFIX} applyUpdatesSaga failed:`, error);
    yield put(UpdateActions.applyUpdates.failure(error.message || 'Failed to apply updates'));
  }
}

function* autoDownloadCudaRuntimeSaga(action: ReturnType<typeof UpdateActions.checkUpdates.success>) {
  try {
    if (!isWindowsElectron()) return;

    const updates = action.payload?.availableUpdates || [];
    const cudaUpdate = updates.find((u: any) => u?.type === 'ml_runtime_cuda');
    if (!cudaUpdate) return;

    const electronAPI = (window as any)?.electronAPI;
    if (!electronAPI?.getMlRuntimeStatus || !electronAPI?.checkCudaCompatibility) return;

    // Do not download if already installed.
    const runtimeStatus = yield call([electronAPI, 'getMlRuntimeStatus']);
    if (runtimeStatus?.success && runtimeStatus?.cudaInstalled) return;

    const compat = yield call([electronAPI, 'checkCudaCompatibility']);
    if (!compat?.success || !compat?.compatible) return;

    // Do not start if already downloading or ready.
    const existingProgress = (yield select((state: any) => state?.update?.downloadProgress?.ml_runtime_cuda)) as any;
    const existingStatus = existingProgress?.status;
    if (existingStatus === 'downloading' || existingStatus === 'ready') return;

    const attemptedVersionRaw = yield call([AsyncStorage, 'getItem'], CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY);
    const attemptedVersion = String(attemptedVersionRaw || '').trim();
    const targetVersion = String(cudaUpdate.version || '').trim();
    const expectedSize = Number(cudaUpdate.size || 0);

    if (attemptedVersion && attemptedVersion === targetVersion) {
      // Allow silent resume on restart if a partial (or fully-downloaded but not registered) file exists.
      try {
        const fileName = `ml_runtime_cuda_${targetVersion}.zip`;
        if (electronAPI?.statUpdateFile && expectedSize > 0) {
          const stat = yield call([electronAPI, 'statUpdateFile'], { fileName });
          const sizeBytes = Number(stat?.sizeBytes || 0);
          if (stat?.success && stat?.exists && sizeBytes > 0 && sizeBytes <= expectedSize) {
            console.log(`${OTA_LOG_PREFIX} Resuming CUDA auto-download for v${targetVersion} (have ${sizeBytes}/${expectedSize})`);
            yield put(UpdateActions.downloadUpdate.request(cudaUpdate));
            return;
          }
        }
      } catch {
        // ignore and fall through to skip
      }
      console.log(`${OTA_LOG_PREFIX} CUDA auto-download already attempted for v${attemptedVersion}, skipping`);
      return;
    }

    // Cleanup: if a newer version is available, delete any older partial for the previously attempted version.
    try {
      if (attemptedVersion && attemptedVersion !== targetVersion && electronAPI?.statUpdateFile) {
        const oldFileName = `ml_runtime_cuda_${attemptedVersion}.zip`;
        const statOld = yield call([electronAPI, 'statUpdateFile'], { fileName: oldFileName });
        if (statOld?.success && statOld?.exists && statOld?.filePath && electronAPI?.deleteFile) {
          yield call([electronAPI, 'deleteFile'], statOld.filePath);
        }
      }
    } catch {
      // ignore cleanup failures
    }

    yield call([AsyncStorage, 'setItem'], CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY, targetVersion);
    console.log(`${OTA_LOG_PREFIX} Auto-downloading CUDA runtime v${targetVersion}`);
    yield put(UpdateActions.downloadUpdate.request(cudaUpdate));
  } catch (error: any) {
    console.warn(`${OTA_LOG_PREFIX} autoDownloadCudaRuntimeSaga failed:`, error?.message || error);
  }
}

export function* updateSaga() {
  yield takeLatest(UpdateActions.CHECK_UPDATES_REQUEST, checkUpdatesSaga);
  yield takeLatest(UpdateActions.CHECK_UPDATES_SUCCESS, autoDownloadCudaRuntimeSaga);
  yield takeLatest(UpdateActions.DOWNLOAD_UPDATE_REQUEST, downloadUpdateSaga);
  yield takeLatest(UpdateActions.CANCEL_DOWNLOAD_REQUEST, cancelDownloadSaga);
  yield takeLatest(UpdateActions.APPLY_UPDATES_REQUEST, applyUpdatesSaga);
}
